/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * Document type identifiers for legal documents.
 */
export type DocumentType =
  | 'service-terms'
  | 'privacy-policy'
  | 'cookie-policy'
  | 'data-storage-terms'
  | 'acceptable-use-policy';

/**
 * Document version metadata.
 */
export interface DocumentVersion {
  version: number;
  published: string;
  lastUpdated: string;
}

/**
 * Document configuration with version information.
 */
export interface DocumentConfig {
  type: DocumentType;
  basePath: string;
  versions: DocumentVersion[];
  defaultLanguage: string;
  filenamePattern: (lang: string, version?: number) => string;
}

/**
 * Document version configurations.
 * This is the single source of truth for document versions.
 */
export const DOCUMENT_CONFIGS: Record<DocumentType, DocumentConfig> = {
  'service-terms': {
    type: 'service-terms',
    basePath: '/serviceterms',
    versions: [
      { version: 1, published: 'January 2025', lastUpdated: 'January 2025' },
      { version: 2, published: 'April 2026', lastUpdated: 'April 2026' },
      { version: 3, published: 'July 2026', lastUpdated: 'July 2026' },
      { version: 4, published: 'October 2026', lastUpdated: 'October 2026' },
      { version: 5, published: 'October 2026', lastUpdated: 'October 2026' },
    ],
    defaultLanguage: 'pt-BR',
    filenamePattern: (lang: string, version?: number) => {
      if (version && version >= 2) {
        const base = `/serviceterms/v${version}/ServiceTermsText`;
        return lang === 'pt-BR' ? `${base}.md` : `${base}-${lang}.md`;
      }
      if (lang === 'pt-BR') {
        return '/ServiceTermsText.md';
      }
      return `/ServiceTermsText-${lang}.md`;
    },
  },
  'privacy-policy': {
    type: 'privacy-policy',
    basePath: '/privacy',
    versions: [
      { version: 1, published: 'January 2025', lastUpdated: 'January 2025' },
      { version: 2, published: 'April 2026', lastUpdated: 'April 2026' },
      { version: 3, published: 'July 2026', lastUpdated: 'July 2026' },
    ],
    defaultLanguage: 'en',
    filenamePattern: (lang: string, version?: number) => {
      if (version && version >= 2) {
        const base = `/privacy/v${version}/PrivacyPolicyText`;
        return lang === 'en' ? `${base}.md` : `${base}-${lang}.md`;
      }
      if (lang === 'en') {
        return '/PrivacyPolicyText.md';
      }
      return `/PrivacyPolicyText-${lang}.md`;
    },
  },
  'cookie-policy': {
    type: 'cookie-policy',
    basePath: '/cookies',
    versions: [
      { version: 1, published: 'January 2025', lastUpdated: 'January 2025' },
      { version: 2, published: 'April 2026', lastUpdated: 'April 2026' },
    ],
    defaultLanguage: 'pt-BR',
    filenamePattern: (lang: string, _version?: number) => {
      if (lang === 'pt-BR') {
        return '/CookiePolicyText-pt-BR.md';
      }
      return `/CookiePolicyText-${lang}.md`;
    },
  },
  'data-storage-terms': {
    type: 'data-storage-terms',
    basePath: '/data-storage',
    versions: [
      { version: 1, published: 'January 2025', lastUpdated: 'January 2025' },
    ],
    defaultLanguage: 'en',
    filenamePattern: (lang: string, _version?: number) => {
      if (lang === 'en') {
        return '/DataProcessingAgreement-en.md';
      }
      return `/DataProcessingAgreement-${lang}.md`;
    },
  },
  'acceptable-use-policy': {
    type: 'acceptable-use-policy',
    basePath: '/acceptable-use',
    versions: [
      { version: 1, published: 'July 2026', lastUpdated: 'July 2026' },
    ],
    defaultLanguage: 'en',
    filenamePattern: (lang: string, _version?: number) => {
      if (lang === 'en') {
        return '/AcceptableUsePolicyText-en.md';
      }
      return `/AcceptableUsePolicyText-${lang}.md`;
    },
  },
};
